import { useAddress } from "@redotech/redo-customer-portal-shared/hooks/useAddress/use-address";
import { MerchantOverrides } from "@redotech/redo-customer-portal-shared/hooks/useReview/use-merchant-overrides";
import { Navigator } from "@redotech/redo-customer-portal-shared/navigator";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { Channel } from "@redotech/redo-model/return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import AlertCircleIcon from "@redotech/redo-web/arbiter-icon/alert-circle.svg";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import {
  SelectDropdown,
  SelectDropdownTheme,
} from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import { SpacingValue } from "@redotech/redo-web/theme/box";
import { useState } from "react";
import { Addresses } from "../../components/addresses";
import * as reviewCss from "./review.module.css";

export const MerchantOverrideDetailsSection = ({
  overrides,
  pxOverride,
}: {
  overrides: MerchantOverrides;
  pxOverride: SpacingValue;
}) => {
  if (!overrides) {
    return null;
  }
  const { texts, inputs } = overrides;
  return (
    <Flex
      className={reviewCss.styledCard}
      flexDirection="column"
      gap="2xl"
      grow={1}
      px={pxOverride}
      py="3xl"
    >
      <Text fontSize="lg" fontWeight="semibold" textColor="primary">
        {texts.detailsHeader}
      </Text>
      <RedoToggle
        description={texts.flagReturnDescription}
        disabled={!inputs.enabled}
        label={texts.flagReturn}
        setValue={inputs.onChangeFlagReturn}
        size="sm"
        value={inputs.flagReturn}
      />
      <RedoToggle
        description={texts.manualReviewDescription}
        disabled={!inputs.enabled}
        label={texts.manualReview}
        setValue={inputs.onChangeManualReview}
        size="sm"
        value={inputs.manualReview}
      />
    </Flex>
  );
};

export const MerchantOverrideShippingLabelPayers = ({
  overrides,
}: {
  overrides: MerchantOverrides;
}) => {
  if (!overrides) {
    return null;
  }
  const {
    texts,
    inputs,
    showLabelPayerToggle,
    showNewOrderLabelPayerToggle,
    showMerchantPaysReturnShippingDescription,
  } = overrides;
  return (
    <Flex flexDirection="column" gap="2xl">
      {showLabelPayerToggle && (
        <RedoToggle
          description={texts.customerPaysReturnShippingDescription}
          disabled={!inputs.enabled}
          label={texts.customerPaysReturnShipping}
          setValue={inputs.onChangeCustomerPaysReturnShipping}
          size="sm"
          value={inputs.customerPaysReturnShipping}
        />
      )}
      {showMerchantPaysReturnShippingDescription && (
        <Text fontSize="xs" textColor="tertiary">
          {texts.merchantPaysReturnShippingDescription}
        </Text>
      )}
      {showNewOrderLabelPayerToggle && (
        <RedoToggle
          description={texts.customerPaysNewOrderShippingDescription}
          disabled={!inputs.enabled}
          label={texts.customerPaysNewOrderShipping}
          setValue={inputs.onChangeCustomerPaysNewOrderShipping}
          size="sm"
          value={inputs.customerPaysNewOrderShipping}
        />
      )}
      <Text fontSize="xs" textColor="tertiary">
        {texts.labelDescription}
      </Text>
    </Flex>
  );
};

export const MerchantOverrideDiscountsSection = ({
  overrides,
  pxOverride,
}: {
  overrides: MerchantOverrides;
  pxOverride: SpacingValue;
}) => {
  if (!overrides || !overrides.showDiscountSection) {
    return null;
  }
  const { texts, inputs } = overrides;
  return (
    <Flex
      className={reviewCss.styledCard}
      flexDirection="column"
      gap="2xl"
      grow={1}
      px={pxOverride}
      py="3xl"
    >
      <Text fontSize="lg" fontWeight="semibold" textColor="primary">
        {texts.discountsHeader}
      </Text>
      <RedoTagInputField
        description={texts.discountInputDescription}
        inputValue={inputs.newDiscountCode}
        label={texts.discountInputLabel}
        setInputValue={inputs.onChangeNewDiscountCode}
        setTags={inputs.onChangeDiscountCodes}
        state={inputs.enabled ? "default" : "disabled"}
        tags={inputs.discountCodes.map((code) => ({ text: code }))}
      />
      <RedoCheckbox
        disabled={!inputs.enabled}
        label={texts.applyAutomaticDiscountsLabel}
        setValue={inputs.onChangeApplyAutomaticDiscounts}
        size="sm"
        value={inputs.applyAutomaticDiscounts}
      />
    </Flex>
  );
};

enum AdjustmentType {
  FEE = "Fee",
  BONUS = "Bonus",
}

enum AdjustmentModeType {
  AMOUNT = "Amount",
  PERCENTAGE = "Percentage",
}

export const MerchantOverrideAdjustmentModal = ({
  overrides,
  isOpen,
  onClose,
  pendingReturnItemId,
  itemReturnValue,
}: {
  overrides: NonNullable<MerchantOverrides>;
  isOpen: boolean;
  onClose: () => void;
  pendingReturnItemId: string | undefined;
  itemReturnValue?: number;
}) => {
  const { texts, inputs } = overrides;
  const adjustmentOverrideValue =
    inputs.adjustmentOverrides.get(pendingReturnItemId ?? "") ?? 0;
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>(
    adjustmentOverrideValue > 0 ? AdjustmentType.BONUS : AdjustmentType.FEE,
  );
  const [adjustmentMode, setAdjustmentMode] = useState<AdjustmentModeType>(
    AdjustmentModeType.AMOUNT,
  );

  const [inputValue, setInputValue] = useState<string>(
    (adjustmentOverrideValue !== 0
      ? Math.abs(adjustmentOverrideValue)
      : 0
    ).toFixed(2),
  );

  const onApply = () => {
    const value = parseFloat(inputValue) || 0;
    const baseValue = itemReturnValue ?? 0;

    // Calculate final amount based on mode
    let finalAmount: number;
    if (adjustmentMode === AdjustmentModeType.PERCENTAGE) {
      finalAmount = (baseValue * value) / 100;
    } else {
      finalAmount = value;
    }

    // Ensure proper 2 decimal place precision for monetary values
    finalAmount = Math.round(finalAmount * 100) / 100;

    inputs.onChangeAdjustmentOverride(
      pendingReturnItemId ?? "",
      adjustmentType === AdjustmentType.BONUS ? finalAmount : -finalAmount,
    );
    onClose();
  };

  const formatInputValue = (value: string) => {
    if (!value || isNaN(parseFloat(value))) {
      setInputValue("0.00");
    } else {
      const parsedValue = parseFloat(value);
      setInputValue(parsedValue >= 0 ? parsedValue.toFixed(2) : "0.00");
    }
  };

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: texts.adjustmentModalApplyButton,
        onClick: onApply,
        size: "sm",
      }}
      secondaryButton={{
        text: texts.adjustmentModalCancelButton,
        onClick: onClose,
        size: "sm",
      }}
      title={texts.adjustmentModalTitle}
    >
      <Flex flexDirection="column" gap="3xl">
        <RedoButtonTabGroup<AdjustmentType>
          onChange={(value: AdjustmentType) => setAdjustmentType(value)}
          options={[
            { value: AdjustmentType.FEE, el: <>{AdjustmentType.FEE}</> },
            { value: AdjustmentType.BONUS, el: <>{AdjustmentType.BONUS}</> },
          ]}
          size="sm"
          value={adjustmentType}
        />
        <LabeledInput label={adjustmentMode}>
          <RedoTextInput
            inputTypeHint="number"
            onBlur={() => formatInputValue(inputValue)}
            setValue={setInputValue}
            size="md"
            state={isNaN(parseFloat(inputValue)) ? "error" : "default"}
            trailingActions={
              <SelectDropdown
                className={reviewCss.inlineCurrencyDropdown}
                display={(mode) => (
                  <Flex align="center" gap="xs">
                    <Text fontSize="sm" textColor="tertiary">
                      {mode === AdjustmentModeType.AMOUNT ? "$" : "%"}
                    </Text>
                  </Flex>
                )}
                options={[
                  AdjustmentModeType.AMOUNT,
                  AdjustmentModeType.PERCENTAGE,
                ]}
                showChevron
                theme={SelectDropdownTheme.GHOST}
                value={adjustmentMode}
                valueChange={(mode) => mode && setAdjustmentMode(mode)}
              >
                {(mode) => (
                  <Text fontSize="xs" textColor="primary">
                    {mode === AdjustmentModeType.AMOUNT ? "$" : "%"}
                  </Text>
                )}
              </SelectDropdown>
            }
            value={inputValue}
          />
        </LabeledInput>
      </Flex>
    </RedoModal>
  );
};

export const MerchantOverrideItemAdjustments = ({
  overrides,
  pendingReturnItemId,
  itemReturnValue,
}: {
  overrides: MerchantOverrides;
  pendingReturnItemId: string | undefined;
  itemReturnValue?: number;
}) => {
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  if (!overrides || !pendingReturnItemId) {
    return null;
  }
  const { texts, inputs } = overrides;
  return (
    <Flex flexDirection="column" gap="lg">
      <Flex style={{ width: "155px" }}>
        <RedoButton
          disabled={!inputs.enabled}
          hierarchy="secondary"
          onClick={() => setIsAdjustmentModalOpen(true)}
          size="sm"
          text={texts.adjustFeeButton}
        />
      </Flex>
      <RedoToggle
        description={texts.greenReturnDescription}
        disabled={!inputs.enabled}
        label={texts.greenReturn}
        setValue={(value) =>
          inputs.onChangeGreenReturnOverride(pendingReturnItemId, value)
        }
        size="sm"
        value={inputs.greenReturnOverrides.get(pendingReturnItemId) ?? false}
      />
      {isAdjustmentModalOpen && (
        <MerchantOverrideAdjustmentModal
          isOpen={isAdjustmentModalOpen}
          itemReturnValue={itemReturnValue}
          onClose={() => setIsAdjustmentModalOpen(false)}
          overrides={overrides}
          pendingReturnItemId={pendingReturnItemId}
        />
      )}
    </Flex>
  );
};

export const MerchantOverrideAddressesSection = ({
  navigation,
  settings,
  channel,
  overrides,
}: {
  navigation: Navigator;
  settings: ReturnAppSettings | undefined;
  channel: Channel;
  overrides: MerchantOverrides;
}) => {
  const { addresses } = useAddress({
    navigator: navigation,
    settings,
    channel,
  });

  return (
    <>
      <Addresses addresses={addresses} showBorder={false} />
      <MerchantOverrideShippingLabelPayers overrides={overrides} />
    </>
  );
};

export const MerchantOverrideItemRejectMessage = ({
  merchantCreatedPreRejectMessage,
}: {
  merchantCreatedPreRejectMessage: string;
}) => {
  return (
    <Flex align="center" gap="xs">
      <RedoFeaturedIcon color="warning" Icon={AlertCircleIcon} size="xs" />
      <Text fontSize="xs" fontWeight="bold" textColor="disabled">
        {merchantCreatedPreRejectMessage}
      </Text>
    </Flex>
  );
};
